import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { useTheme } from 'styled-components';
import { useAuthStore } from '@stores/auth.store';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { useProjectMilestones } from '@hooks/projects/useProjectMilestones';
import { useProjectSprints } from '@hooks/projects/useProjectSprints';
import { useProjectBugs } from '@hooks/projects/useProjectBugs';
import { useProjectBlockers } from '@hooks/projects/useProjectBlockers';
import { isDiscussionContentEmpty } from '@hooks/projects/projectHelpers';
import { getAvatarColor } from '@utils/avatarColor';
import { contentToApiMentions } from '@utils/richTextMentions';
import http from '@utils/http';
import { usePublicTags } from '@hooks/public/useTags';
import { resolveAttachmentUrl, readFileAsDataUrl } from './utils/discussionPanel.utils';

const MENTION_USERS_LIMIT = 100;

function isAttachmentImage(att) {
    const mime = (att?.type || att?.mimeType || '').toLowerCase();
    const ref = att?.url || att?.href || att?.fileUrl || '';
    const name = att?.title ?? att?.name ?? ref;
    return (
        mime.startsWith('image/') ||
        ref.startsWith('data:image/') ||
        /\.(jpe?g|png|gif|webp|bmp)$/i.test(name || ref || '')
    );
}

export function useDiscussionPanel({
    discussions,
    isLoading,
    createPost,
    createPostPending,
    projectId,
    timeFormat,
    feedAccent,
    variant,
    zIndexBase,
    editPost,
    editPostPending,
    deletePost,
    deletePostPending,
}) {
    const theme = useTheme();
    const dark = theme?.colors?.background?.default === '#141414';
    const currentUser = useAuthStore((s) => s.user);
    const currentUserId = currentUser?.id ?? null;
    const currentUserName = currentUser?.name ?? currentUser?.email ?? '';

    const { data: project } = useProjectDetail(projectId);
    const { milestones: projectMilestones = [] } = useProjectMilestones(projectId ?? '');
    const { sprints: projectSprints = [] } = useProjectSprints(projectId ?? '', { enabled: !!projectId });
    const { bugs: projectBugs = [] } = useProjectBugs(projectId ?? '');
    const { blockers: projectBlockers = [] } = useProjectBlockers(projectId ?? '');
    const { tags: publicTags } = usePublicTags({ enabled: true });

    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT),
        [project?.assignedUsers],
    );
    const mentionMilestones = useMemo(
        () => (Array.isArray(projectMilestones) ? projectMilestones : []),
        [projectMilestones],
    );
    const mentionSprints = useMemo(
        () => (Array.isArray(projectSprints) ? projectSprints : []),
        [projectSprints],
    );
    const flatTasks = useMemo(() => {
        const sprints = Array.isArray(projectSprints) ? projectSprints : [];
        return sprints.flatMap((s) => (Array.isArray(s.tasks) ? s.tasks : []).map((t) => ({ ...t })));
    }, [projectSprints]);

    const discussionsForList = useMemo(() => {
        if (!Array.isArray(discussions)) return [];
        return variant === 'chat' ? [...discussions].reverse() : discussions;
    }, [discussions, variant]);

    const messageListRef = useRef(null);
    const scrollAnchorRef = useRef(null);
    const discussionComposeRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        const el = messageListRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    useLayoutEffect(() => {
        if (variant !== 'chat') return;
        if (isLoading) return;
        scrollToBottom();
        const t = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(t);
    }, [variant, isLoading, discussionsForList.length, scrollToBottom]);

    useEffect(() => {
        if (variant !== 'chat') return;
        const el = messageListRef.current;
        if (!el) return;
        const observer = new MutationObserver(() => {
            const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
            if (isNearBottom) scrollToBottom();
        });
        observer.observe(el, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [variant, scrollToBottom]);
    
    const [editingId, setEditingId] = useState(null);
    const [editingOriginalText, setEditingOriginalText] = useState('');
    const [initialFiles, setInitialFiles] = useState([]);

    const handleChatSend = useCallback(
        (markdown, files, linkAttachments) => {
            const text = (markdown || '').trim();
            if (editingId && editPost) {
                // When editing in chat variant, MessageCompose managed its own combined list.
                // But in this hook, handleChatSend is called with the final lists.
                
                // We need to get the kept existing attachments too.
                // I need to update MessageCompose to pass them to onSend.
                // Wait, if I don't update MessageCompose's onSend call, I only get 'files'.
                
                // I'll update MessageCompose's handleSend to also pass existingAttachments.
                
                editPost(
                    editingId,
                    { 
                        content: text,
                        files: files,
                        attachments: linkAttachments // linkAttachments will contain kept existing attachments in chat variant if I update it
                    },
                    {
                        onSuccess: () => {
                            message.success('Updated');
                            setEditingId(null);
                            setEditingOriginalText('');
                            setInitialFiles([]);
                            discussionComposeRef.current?.clear?.();
                            setTimeout(() => scrollToBottom(), 150);
                        },
                        onError: (err) => {
                            message.error(err?.response?.data?.message || err?.message || 'Failed to update');
                            cancelEdit();
                        },
                    }
                );
                return;
            }

            const fileList = Array.isArray(files) ? files : [];
            const links = Array.isArray(linkAttachments) ? linkAttachments : [];
            const hasContent = text || fileList.length > 0 || links.length > 0;
            if (!hasContent || !createPost) return;
            createPost(
                {
                    content: text,
                    attachments: links,
                    files: fileList.length ? fileList : undefined,
                },
                {
                    onSuccess: () => {
                        message.success('Posted');
                        discussionComposeRef.current?.clear?.();
                        setTimeout(() => scrollToBottom(), 150);
                    },
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to post'),
                },
            );
        },
        [createPost, editPost, editingId, scrollToBottom],
    );

    const discussionHeaderAvatar = useMemo(
        () => getAvatarColor(projectId ?? project?.name ?? 'discussion'),
        [projectId, project?.name],
    );

    const [form] = Form.useForm();
    const [pendingPostAttachments, setPendingPostAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const [attachmentPreviewLoading, setAttachmentPreviewLoading] = useState(false);


    const handlePostFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({ id: `post-${Date.now()}-${i}`, file, name: file.name, type: file.type }));
        setPendingPostAttachments((prev) => [...prev, ...next]);
        (async () => {
            const withPreviews = await Promise.all(
                next.map(async (item) => {
                    if ((item.type || '').startsWith('image/')) {
                        try {
                            return { ...item, dataUrl: await readFileAsDataUrl(item.file) };
                        } catch {
                            return item;
                        }
                    }
                    return item;
                }),
            );
            setPendingPostAttachments((prev) =>
                prev.map((p) => {
                    const w = withPreviews.find((x) => x.id === p.id);
                    return w ? { ...p, dataUrl: w.dataUrl } : p;
                }),
            );
        })();
    }, []);

    const removePendingPostAttachmentByIndex = useCallback((index) => {
        setPendingPostAttachments((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const removePendingPostAttachment = useCallback(
        (id) => {
            const index = pendingPostAttachments.findIndex((p) => p.id === id);
            if (index < 0) {
                setPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
                return;
            }
            let content = form.getFieldValue('content') || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href=\"#attachment-${index}\")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            const total = pendingPostAttachments.length;
            for (let j = total - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            form.setFieldValue('content', content);
            setPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
        },
        [form, pendingPostAttachments],
    );

    const handleLinkRequest = useCallback((insertLink) => {
        linkModalCallbackRef.current = insertLink;
        linkModalForm.resetFields();
        setLinkModalOpen(true);
    }, [linkModalForm]);

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setPendingLinkAttachments((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const handleEditClick = useCallback((post) => {
        const id = post.id ?? post.discussionId;
        let raw = post.content ?? post.body ?? '';
        if (typeof raw !== 'string') raw = String(raw || '');
        setEditingId(id);
        
        // Populate existing attachments
        const existing = (post.attachments ?? []).map((att) => ({
            id: att.id,
            name: att.name || att.title || 'Attachment',
            type: att.mimeType || att.type || '',
            url: att.url || att.path,
            isExisting: true
        }));
        setInitialFiles(existing);
        setPendingPostAttachments(existing);
        
        // Use a plain-text-ish version for the preview bar
        const preview = raw.replace(/<[^>]+>/g, ' ').trim();
        setEditingOriginalText(preview.length > 80 ? `${preview.slice(0, 80)}...` : preview);
        
        form.setFieldValue('content', raw);
        if (variant === 'chat') {
            discussionComposeRef.current?.setPlainText?.(raw);
        } else {
            // Stacked variant scroll to top where the editor is
            const el = messageListRef.current;
            if (el) el.scrollTop = 0;
        }
    }, [variant, form]);

    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setEditingOriginalText('');
        setInitialFiles([]);
        setPendingPostAttachments([]);
        form.setFieldValue('content', '');
        if (variant === 'chat') {
            discussionComposeRef.current?.clear?.();
        }
    }, [form, variant]);

    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            const raw = values.content ?? '';
            const contentRaw = typeof raw === 'string' ? raw.trim() : '';
            const content = contentToApiMentions(contentRaw);
            const hasContent = content && !isDiscussionContentEmpty(content);
            const files = pendingPostAttachments.map((p) => p.file);
            const linkAttachments = pendingLinkAttachments.map(({ url, title }) => ({ url, title }));
            const hasLinks = linkAttachments.length > 0;
            if (!hasContent && !files.length && !hasLinks) {
                message.warning('Enter a message, add a link, or attach a file');
                return;
            }

            if (editingId && editPost) {
                const newFiles = pendingPostAttachments.filter(p => !p.isExisting).map(p => p.file);
                const keptAttachments = pendingPostAttachments.filter(p => p.isExisting).map(p => ({
                    id: p.id,
                    name: p.name,
                    url: p.url,
                    type: p.type
                }));

                editPost(
                    editingId,
                    { 
                        content: content || '',
                        attachments: [...keptAttachments, ...linkAttachments],
                        files: newFiles.length ? newFiles : undefined
                    },
                    {
                        onSuccess: () => {
                            message.success('Updated');
                            setEditingId(null);
                            setEditingOriginalText('');
                            setInitialFiles([]);
                            setPendingPostAttachments([]);
                            setPendingLinkAttachments([]);
                            form.setFieldValue('content', '');
                        },
                        onError: (err) => {
                            message.error(err?.response?.data?.message || err?.message || 'Failed to update');
                            cancelEdit();
                        },
                    }
                );
                return;
            }

            createPost(
                {
                    content: content || '',
                    attachments: linkAttachments,
                    files: files.length ? files : undefined,
                },
                {
                    onSuccess: () => {
                        message.success('Posted');
                        form.setFieldValue('content', '');
                        setPendingPostAttachments([]);
                        setPendingLinkAttachments([]);
                    },
                    onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to post'),
                },
            );
        });
    }, [form, createPost, editPost, editingId, pendingPostAttachments, pendingLinkAttachments]);

    const handleAttachmentClick = useCallback(
        async (att) => {
            const raw = att?.url ?? att?.href ?? att?.fileUrl ?? att?.path;
            const name = att?.title ?? att?.name ?? 'download';
            if (typeof raw === 'string' && raw.startsWith('#attachment-')) {
                const pending = pendingPostAttachments.find((p) => (p.name || '').trim() === (name || '').trim());
                if (!pending) return;
                if ((pending.type || '').startsWith('image/') && (pending.dataUrl || pending.file)) {
                    const url = pending.dataUrl || (pending.file && URL.createObjectURL(pending.file));
                    if (url) setAttachmentPreview({ url, name: pending.name, type: 'image' });
                    return;
                }
                if (pending.file) {
                    window.open(URL.createObjectURL(pending.file), '_blank', 'noopener,noreferrer');
                }
                return;
            }
            if (!raw) return;
            const url = resolveAttachmentUrl(raw);
            if (isAttachmentImage(att)) {
                setAttachmentPreviewLoading(true);
                try {
                    const { data } = await http.get(url, { responseType: 'blob' });
                    setAttachmentPreview({ url: URL.createObjectURL(data), name, type: 'image' });
                } catch (err) {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to load image');
                } finally {
                    setAttachmentPreviewLoading(false);
                }
                return;
            }
            // Non-image attachments: keep behavior as a new tab.
            window.open(url, '_blank', 'noopener,noreferrer');
        },
        [pendingPostAttachments],
    );

    const handleCloseAttachmentPreview = useCallback(() => {
        if (attachmentPreview?.url?.startsWith?.('blob:')) {
            URL.revokeObjectURL(attachmentPreview.url);
        }
        setAttachmentPreview(null);
        setAttachmentPreviewLoading(false);
    }, [attachmentPreview?.url]);

    const popupZIndexBase = (typeof zIndexBase === 'number' ? zIndexBase : 1050) + 5000;
    const modalZIndex = (typeof zIndexBase === 'number' ? zIndexBase : 1050) + 10000;

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [postIdToDelete, setPostIdToDelete] = useState(null);

    const confirmDelete = useCallback((postId) => {
        setPostIdToDelete(postId);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!postIdToDelete || !deletePost) return;
        deletePost(postIdToDelete);
        setDeleteModalOpen(false);
        setPostIdToDelete(null);
    }, [postIdToDelete, deletePost]);

    return {
        dark,
        project,
        currentUserId,
        currentUserName,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        flatTasks,
        projectBugs,
        projectBlockers,
        publicTags,
        discussionsForList,
        messageListRef,
        scrollAnchorRef,
        discussionComposeRef,
        discussionHeaderAvatar,
        handleChatSend,
        form,
        pendingPostAttachments,
        pendingLinkAttachments,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        linkModalCallbackRef,
        attachmentPreview,
        attachmentPreviewLoading,
        handlePostFilesSelected,
        removePendingPostAttachmentByIndex,
        removePendingPostAttachment,
        handleLinkRequest,
        handleLinkModalSubmit,
        handleSubmit,
        handleAttachmentClick,
        handleCloseAttachmentPreview,
        popupZIndexBase,
        modalZIndex,
        createPostPending,
        isLoading,
        feedAccent,
        timeFormat,
        editPost,
        editPostPending,
        deletePost,
        deletePostPending,
        editingId,
        editingOriginalText,
        handleEditClick,
        cancelEdit,
        initialFiles,
        deleteModalOpen,
        setDeleteModalOpen,
        confirmDelete,
        handleDeleteConfirm,
    };
}

